import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useAuth } from "~~/app/composables/useAuth";

export const useLoginStore = defineStore("loginStore", () => {
  const route = useRoute();
  const isLoading = ref(false);
  const loginAttempts = ref(0);
  const lastLoginAttempt = ref<number | null>(null);

  const MAX_LOGIN_ATTEMPTS = 5;
  const RATE_LIMIT_WINDOW = 15 * 60 * 1000;

  const isRateLimited = computed(() => {
    if (!lastLoginAttempt.value) return false;
    const timeSinceLastAttempt = Date.now() - lastLoginAttempt.value;
    return (
      loginAttempts.value >= MAX_LOGIN_ATTEMPTS &&
      timeSinceLastAttempt < RATE_LIMIT_WINDOW
    );
  });

  const rateLimitTimeRemaining = computed(() => {
    if (!isRateLimited.value || !lastLoginAttempt.value) return 0;
    const timeSinceLastAttempt = Date.now() - lastLoginAttempt.value;
    return Math.max(0, RATE_LIMIT_WINDOW - timeSinceLastAttempt);
  });

  const handleLogin = async (identifier: string, password: string) => {
    const { login } = useAuth();
    if (isLoading.value) return;

    if (isRateLimited.value) {
      const remainingMinutes = Math.ceil(
        rateLimitTimeRemaining.value / 1000 / 60,
      );
      throw new Error(
        `Too many login attempts. Please try again in ${remainingMinutes} minutes.`,
      );
    }

    if (!identifier?.trim() || !password?.trim()) {
      throw new Error("Email/mobile and password are required");
    }
    isLoading.value = true;
    const credentials = {
      identifier: identifier.trim(),
      password,
    };
    const redirectPath = route.query.redirect as string | undefined;
    await login(credentials, redirectPath);
    isLoading.value = false;
  };

  const handleEmailLoginSubmit = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    return handleLogin(email, password);
  };

  const handleMobileLoginSubmit = async ({
    mobile,
    password,
  }: {
    mobile: string;
    password: string;
  }) => {
    return handleLogin(mobile, password);
  };

  return {
    // State
    user: useAuth().user,
    error: useAuth().error,
    isLoading,
    loginAttempts: readonly(loginAttempts),
    isRateLimited,
    rateLimitTimeRemaining,

    // Actions
    handleEmailLoginSubmit,
    handleMobileLoginSubmit,
    handleLogin,
  };
});
